import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Checkbox,
  Box,
  Typography,
} from '@mui/material';
import { SamplingPlanningData } from '../hooks/types';

interface SimpleSamplingTableProps {
  data: SamplingPlanningData[];
  onDataChange: (updatedData: SamplingPlanningData[]) => void;
}

function SimpleSamplingTable({ data, onDataChange }: SimpleSamplingTableProps) {
  const handleFieldChange = (
    index: number,
    field: keyof SamplingPlanningData,
    value: string | boolean | number
  ) => {
    const updatedData = [...data];
    updatedData[index] = {
      ...updatedData[index],
      [field]: value,
    };
    onDataChange(updatedData);
  };

  const handleNumberFieldBlur = (
    index: number,
    field: keyof SamplingPlanningData,
    value: string
  ) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      handleFieldChange(index, field, parseFloat(numValue.toFixed(3)));
    }
  };

  const headerCellStyle = {
    padding: '8px 12px',
    background: '#E7EFF5',
    borderRadius: '4px',
    fontFamily: "'Lato', sans-serif",
    fontSize: '12px',
    fontWeight: 700,
    color: 'rgba(0, 0, 0, 0.8)',
    textAlign: 'center',
    border: 'none',
    height: '32px'
  };

  return (
    <TableContainer component={Paper} sx={{ mt: 2, boxShadow: 'none' }} data-testid="simple-sampling-table-container">
      <Table size="small" sx={{ minWidth: 1200 }} data-testid="simple-sampling-table">
        <TableHead data-testid="simple-sampling-table-head">
          <TableRow data-testid="simple-sampling-table-header-row">
            <TableCell sx={headerCellStyle} data-testid="simple-sampling-table-header-designated-operator">Designated Operator</TableCell>
            <TableCell sx={headerCellStyle} data-testid="simple-sampling-table-header-perform-sample-planning">Perform Sample Planning</TableCell>
            <TableCell sx={headerCellStyle} data-testid="simple-sampling-table-header-accuracy-target-p">
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '10px' }}>
                  Accuracy Target P %
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                  <Typography variant="caption" sx={{ fontSize: '8px' }}>Prio</Typography>
                  <Typography variant="caption" sx={{ fontSize: '8px' }}>Non-Prio</Typography>
                </Box>
              </Box>
            </TableCell>
            <TableCell sx={headerCellStyle} data-testid="simple-sampling-table-header-accuracy-target-g">
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '10px' }}>
                  Accuracy Target G %
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                  <Typography variant="caption" sx={{ fontSize: '8px' }}>Prio</Typography>
                  <Typography variant="caption" sx={{ fontSize: '8px' }}>Non-Prio</Typography>
                </Box>
              </Box>
            </TableCell>
            <TableCell sx={headerCellStyle} data-testid="simple-sampling-table-header-accuracy-target-e">
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '10px' }}>
                  Accuracy Target E %
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                  <Typography variant="caption" sx={{ fontSize: '8px' }}>Prio</Typography>
                  <Typography variant="caption" sx={{ fontSize: '8px' }}>Non-Prio</Typography>
                </Box>
              </Box>
            </TableCell>
            <TableCell sx={headerCellStyle} data-testid="simple-sampling-table-header-accuracy-target-x">
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '10px' }}>
                  Accuracy Target X %
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                  <Typography variant="caption" sx={{ fontSize: '8px' }}>Prio</Typography>
                  <Typography variant="caption" sx={{ fontSize: '8px' }}>Non-Prio</Typography>
                </Box>
              </Box>
            </TableCell>
            <TableCell sx={headerCellStyle} data-testid="simple-sampling-table-header-accuracy-target-total">
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '10px' }}>
                  Accuracy Target Total %
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                  <Typography variant="caption" sx={{ fontSize: '8px' }}>Prio</Typography>
                  <Typography variant="caption" sx={{ fontSize: '8px' }}>Non-Prio</Typography>
                </Box>
              </Box>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody data-testid="simple-sampling-table-body">
          {data.map((row, index) => (
            <TableRow key={row.id || index} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' } }} data-testid={`simple-sampling-table-row-${index}`}>
              {/* Designated Operator */}
              <TableCell sx={{ textAlign: 'center' }} data-testid={`simple-sampling-table-cell-designated-operator-${index}`}>{row.designatedOperator}</TableCell>

              {/* Perform Sample Planning */}
              <TableCell sx={{ textAlign: 'center' }} data-testid={`simple-sampling-table-cell-perform-sample-planning-${index}`}>
                <Checkbox
                  checked={row.performSamplePlanning}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleFieldChange(index, 'performSamplePlanning', e.target.checked)
                  }
                  size="small"
                  data-testid={`simple-sampling-table-checkbox-${index}`}
                />
              </TableCell>

              {/* Accuracy Target P % */}
              <TableCell sx={{ textAlign: 'center', padding: '4px' }} data-testid={`simple-sampling-table-cell-accuracy-target-p-${index}`}>
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center'   }}>
                  <TextField
                    size="small"
                    type="number"
                    value={row.accuracyTargetPPrio}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange(index, 'accuracyTargetPPrio', e.target.value)}
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleNumberFieldBlur(index, 'accuracyTargetPPrio', e.target.value)}
                    inputProps={{
                      step: "0.001",
                      min: "0",
                      max: "100",
                    }}
                    variant="outlined"
                    sx={{ 
                      minWidth: 60,
                      '& .MuiInputBase-input': { textAlign: 'center', fontSize: '12px', padding: '6px' }
                    }}
                    data-testid={`simple-sampling-table-input-accuracy-target-p-prio-${index}`}
                  />
                  <TextField
                    size="small"
                    type="number"
                    value={row.accuracyTargetPNonPrio}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange(index, 'accuracyTargetPNonPrio', e.target.value)}
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleNumberFieldBlur(index, 'accuracyTargetPNonPrio', e.target.value)}
                    inputProps={{
                      step: "0.001",
                      min: "0",
                      max: "100",
                    }}
                    variant="outlined"
                    sx={{ 
                      minWidth: 60,
                      '& .MuiInputBase-input': { textAlign: 'center', fontSize: '12px', padding: '6px' }
                    }}
                    data-testid={`simple-sampling-table-input-accuracy-target-p-non-prio-${index}`}
                  />
                </Box>
              </TableCell>

              {/* Accuracy Target G % */}
              <TableCell sx={{ textAlign: 'center', padding: '4px' }}>
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                  <TextField
                    size="small"
                    type="number"
                    value={row.accuracyTargetGPrio}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange(index, 'accuracyTargetGPrio', e.target.value)}
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleNumberFieldBlur(index, 'accuracyTargetGPrio', e.target.value)}
                    inputProps={{
                      step: "0.001",
                      min: "0",
                      max: "100",
                    }}
                    variant="outlined"
                    sx={{ 
                      minWidth: 60,
                      '& .MuiInputBase-input': { textAlign: 'center', fontSize: '12px', padding: '6px' }
                    }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    value={row.accuracyTargetGNonPrio}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange(index, 'accuracyTargetGNonPrio', e.target.value)}
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleNumberFieldBlur(index, 'accuracyTargetGNonPrio', e.target.value)}
                    inputProps={{
                      step: "0.001",
                      min: "0",
                      max: "100",
                    }}
                    variant="outlined"
                    sx={{ 
                      minWidth: 60,
                      '& .MuiInputBase-input': { textAlign: 'center', fontSize: '12px', padding: '6px' }
                    }}
                  />
                </Box>
              </TableCell>

              {/* Accuracy Target E % */}
              <TableCell sx={{ textAlign: 'center', padding: '4px' }}>
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                  <TextField
                    size="small"
                    type="number"
                    value={row.accuracyTargetEPrio}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange(index, 'accuracyTargetEPrio', e.target.value)}
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleNumberFieldBlur(index, 'accuracyTargetEPrio', e.target.value)}
                    inputProps={{
                      step: "0.001",
                      min: "0",
                      max: "100",
                    }}
                    variant="outlined"
                    sx={{ 
                      minWidth: 60,
                      '& .MuiInputBase-input': { textAlign: 'center', fontSize: '12px', padding: '6px' }
                    }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    value={row.accuracyTargetENonPrio}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange(index, 'accuracyTargetENonPrio', e.target.value)}
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleNumberFieldBlur(index, 'accuracyTargetENonPrio', e.target.value)}
                    inputProps={{
                      step: "0.001",
                      min: "0",
                      max: "100",
                    }}
                    variant="outlined"
                    sx={{ 
                      minWidth: 60,
                      '& .MuiInputBase-input': { textAlign: 'center', fontSize: '12px', padding: '6px' }
                    }}
                  />
                </Box>
              </TableCell>

              {/* Accuracy Target X % */}
              <TableCell sx={{ textAlign: 'center', padding: '4px' }}>
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                  <TextField
                    size="small"
                    type="number"
                    value={row.accuracyTargetXPrio}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange(index, 'accuracyTargetXPrio', e.target.value)}
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleNumberFieldBlur(index, 'accuracyTargetXPrio', e.target.value)}
                    inputProps={{
                      step: "0.001",
                      min: "0",
                      max: "100",
                    }}
                    variant="outlined"
                    sx={{ 
                      minWidth: 60,
                      '& .MuiInputBase-input': { textAlign: 'center', fontSize: '12px', padding: '6px' }
                    }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    value={row.accuracyTargetXNonPrio}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange(index, 'accuracyTargetXNonPrio', e.target.value)}
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleNumberFieldBlur(index, 'accuracyTargetXNonPrio', e.target.value)}
                    inputProps={{
                      step: "0.001",
                      min: "0",
                      max: "100",
                    }}
                    variant="outlined"
                    sx={{ 
                      minWidth: 60,
                      '& .MuiInputBase-input': { textAlign: 'center', fontSize: '12px', padding: '6px' }
                    }}
                  />
                </Box>
              </TableCell>

              {/* Accuracy Target Total % */}
              <TableCell sx={{ textAlign: 'center', padding: '4px' }}>
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                  <TextField
                    size="small"
                    type="number"
                    value={row.accuracyTargetTotalPrio}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange(index, 'accuracyTargetTotalPrio', e.target.value)}
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleNumberFieldBlur(index, 'accuracyTargetTotalPrio', e.target.value)}
                    inputProps={{
                      step: "0.001",
                      min: "0",
                      max: "100",
                    }}
                    variant="outlined"
                    sx={{ 
                      minWidth: 60,
                      '& .MuiInputBase-input': { textAlign: 'center', fontSize: '12px', padding: '6px' }
                    }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    value={row.accuracyTargetTotalNonPrio}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange(index, 'accuracyTargetTotalNonPrio', e.target.value)}
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleNumberFieldBlur(index, 'accuracyTargetTotalNonPrio', e.target.value)}
                    inputProps={{
                      step: "0.001",
                      min: "0",
                      max: "100",
                    }}
                    variant="outlined"
                    sx={{ 
                      minWidth: 60,
                      '& .MuiInputBase-input': { textAlign: 'center', fontSize: '12px', padding: '6px' }
                    }}
                  />
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default SimpleSamplingTable; 